import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Save, Upload, X, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fixed banner dimensions
const BANNER_DIMENSIONS = {
  top: { width: 728, height: 90, label: 'Top Banner (728x90)' },
  left: { width: 160, height: 600, label: 'Left Side (160x600)' },
  right: { width: 160, height: 600, label: 'Right Side (160x600)' }
};

const AdsTab = ({ ads, setAds, saving, onSave, token }) => {
  const [uploading, setUploading] = useState({});
  const [errors, setErrors] = useState({});
  const fileInputRefs = useRef({});

  const updateAd = (position, field, value) => {
    setAds(ads.map(ad => ad.position === position ? { ...ad, [field]: value } : ad));
  };

  const validateImageDimensions = (file, position) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const expected = BANNER_DIMENSIONS[position];

        if (img.width === expected.width && img.height === expected.height) {
          resolve(true);
        } else {
          reject(`Image must be exactly ${expected.width}x${expected.height}px. Your image is ${img.width}x${img.height}px.`);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject('Failed to load image');
      };

      img.src = objectUrl;
    });
  };

  const handleBannerUpload = async (e, position) => {
    const file = e.target.files[0];
    if (!file) return;

    setErrors(prev => ({ ...prev, [position]: null }));

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [position]: 'Invalid file type. Upload PNG, JPG, GIF or WebP.' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [position]: 'File too large. Maximum size is 5MB.' }));
      return;
    }

    try {
      await validateImageDimensions(file, position);
    } catch (error) {
      setErrors(prev => ({ ...prev, [position]: error }));
      return;
    }

    setUploading(prev => ({ ...prev, [position]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const imageUrl = response.data.url.startsWith('http')
        ? response.data.url
        : `${BACKEND_URL}${response.data.url}`;

      updateAd(position, 'image_url', imageUrl);
    } catch (error) {
      console.error('Upload error:', error);
      setErrors(prev => ({ ...prev, [position]: 'Failed to upload banner' }));
    } finally {
      setUploading(prev => ({ ...prev, [position]: false }));
      if (fileInputRefs.current[position]) {
        fileInputRefs.current[position].value = '';
      }
    }
  };

  const removeBanner = (position) => {
    updateAd(position, 'image_url', '');
    setErrors(prev => ({ ...prev, [position]: null }));
  };

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Ad Banners</h2>
        <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-6">
        {ads.map((ad) => {
          const dimensions = BANNER_DIMENSIONS[ad.position];
          const isTopBanner = ad.position === 'top';

          return (
            <div key={ad.position} className="bg-zinc-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium">
                  {dimensions.label}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-400 text-sm">Active</span>
                  <Switch
                    checked={ad.active}
                    onCheckedChange={(checked) => updateAd(ad.position, 'active', checked)}
                  />
                </div>
              </div>

              <div className={`grid ${isTopBanner ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4`}>
                <div className="space-y-3">
                  <Label className="text-zinc-400">Banner Image</Label>

                  {ad.image_url ? (
                    <div className="relative bg-zinc-700 rounded-lg p-3">
                      <img
                        src={ad.image_url}
                        alt={`${ad.position} banner`}
                        className={`mx-auto rounded ${isTopBanner ? 'max-h-24' : 'max-h-40'}`}
                        style={{ maxWidth: '100%' }}
                      />
                      <button
                        onClick={() => removeBanner(ad.position)}
                        className="absolute top-2 right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                      <p className="text-center text-zinc-500 text-xs mt-2">
                        {dimensions.width}x{dimensions.height}
                      </p>
                    </div>
                  ) : (
                    <label className={`flex flex-col items-center justify-center border-2 border-dashed border-zinc-600 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors bg-zinc-700/50 ${isTopBanner ? 'p-4' : 'p-6'}`}>
                      <Upload className="w-8 h-8 text-zinc-500 mb-2" />
                      <span className="text-zinc-400 text-sm text-center">Click to upload</span>
                      <span className="text-emerald-400 text-xs mt-1 font-medium">
                        Required: {dimensions.width}x{dimensions.height}px
                      </span>
                      <input
                        ref={el => fileInputRefs.current[ad.position] = el}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        onChange={(e) => handleBannerUpload(e, ad.position)}
                        className="hidden"
                        disabled={uploading[ad.position]}
                      />
                    </label>
                  )}

                  {uploading[ad.position] && (
                    <p className="text-emerald-400 text-sm text-center">Uploading...</p>
                  )}

                  {errors[ad.position] && (
                    <div className="flex items-start gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-red-400 text-sm">{errors[ad.position]}</p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label className="text-zinc-500 text-xs">Or paste URL:</Label>
                    <Input
                      value={ad.image_url}
                      onChange={(e) => updateAd(ad.position, 'image_url', e.target.value)}
                      className="bg-zinc-700 border-zinc-600 text-white text-sm"
                      placeholder="https://example.com/banner.jpg"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-zinc-400">Click URL (Link)</Label>
                  <Input
                    value={ad.link_url}
                    onChange={(e) => updateAd(ad.position, 'link_url', e.target.value)}
                    className="bg-zinc-700 border-zinc-600 text-white"
                    placeholder="https://example.com"
                  />
                  <p className="text-zinc-600 text-xs">
                    Redirect URL when the banner is clicked
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
        <h4 className="text-zinc-300 font-medium mb-2">Banner Size Requirements</h4>
        <ul className="text-zinc-500 text-sm space-y-1">
          <li>• <span className="text-zinc-400">Top Banner:</span> Exactly 728x90 pixels</li>
          <li>• <span className="text-zinc-400">Side Banners:</span> Exactly 160x600 pixels</li>
          <li>• <span className="text-zinc-400">Formats:</span> PNG, JPG, GIF, WebP (max. 5MB)</li>
        </ul>
      </div>
    </div>
  );
};

export default AdsTab;
